import { ActivitySpinnerWidget } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { Location } from '../../../common/react/Location';
import { CreateExplorerEvent } from '../../../common/telemetryEvents/TelemetryEventCreator';
import { AdvancedFilterPanelToggleEvent, ShowExplorer, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { ContentLocation, ExploreDisplayMode, FavoriteToggleType } from '../../../common/telemetryEvents/types/ContentTelemetryTypes';
import { generateTelemetryEventHandler } from '../../../common/telemetryEvents/util/ContentTelemetry';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { ExplorableItemDefinitions } from '../../../ts/models/ContentDefinitions';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { DisplayMode } from '../../../ts/ServerApi/types/ISessionInfo';
import { Telemetry } from '../../../commonInfra/services/Telemetry';
import { ActionMenuItemClickedChildFunction, ActionMenuItemClickedTelemetryContext, CardPositionContext, ContentClickedChildFunction, ContentClickedTelemetryContext, ContentClickedTelemetryContextValueType, ContentInfoContext, ExplorePageDisplayModeContext, FavoriteToggleClickedChildFunction, FavoriteToggleClickedTelemetryContext, RelatedContentInfoContext, SearchTermDataContext } from '../../contexts/TelemetryContexts';
import { ItemsSet } from '../../ItemsSet';
import { FilterMap } from '../../state/ContentExplorerState';
import { contentTypeToWorkbookSubspaceName, isAllServerUsersSpace, isGroupSpace, isProjectSpace, isSiteSpace, isSiteUserContentSubspace, isSiteUsersSpace, isSomeKindaSearchSpace, IsWorkbookDatasourceSubspaceTab, SpaceInfo, WorkbookSubspaceName } from '../../state/SpaceInfo';
import { ConnectedCardView } from '../CardView/CardView';
import { ExplorerEmptyState, ExplorerEmptyStateProps } from '../Components/ExplorerEmpyState';
import { ConnectedListView } from '../ListView/ConnectedListView';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { getWorkbookUrl } from '../WorkbookSpace/WorkbookSpace';
import { AdvancedFilterPanel } from './AdvancedFilterPanel';
import { DatasourceViewTypeInfo } from './DatasourceViewTypeDropdown';
import { ExplorerStyles } from './ExplorerStyles';
import { ContentSpecificControls, ExplorerToolbar } from './ExplorerToolbar';
import { ServerUsersAdvancedFilterPanel } from './ServerUsersAdvancedFilterPanel';
import { SiteUsersAdvancedFilterPanel } from './SiteUsersAdvancedFilterPanel';
import { ServerSettingsUnauthenticated } from '../../../settings/services/ServerSettingsUnauthenticated';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';

const FILTER_ICON_LARGE_PATH = imgPath('spaces_filter_large.svg');

export interface ExplorerStateProps {
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly mixedContentFilterMap: FilterMap;
  readonly items: ReadonlyArray<IExplorableItemTypeInfo>;
  readonly isBusyFetchingItems: boolean;
  readonly selectedItems: ItemsSet<IExplorableItemTypeInfo>;
  readonly isSelectionBusy: boolean;
  readonly contentSort: ReadonlyArray<IOrderClause>;
  readonly explorerViewMode: DisplayMode;
  readonly showFilterPanel: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly urls: SpacesSiteUrls;
  readonly catalogingOnSiteEnabled: boolean;
}

export interface ExplorerDispatchProps {
  readonly selectItem: (item: IContentInfo) => void;
  readonly deselectItem: (item: IContentInfo) => void;
  readonly clearAllSelections: () => void;
  readonly selectAll: () => void;
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly changeExplorerViewMode: (viewMode: DisplayMode, saveToServer: boolean) => void;
  readonly updateFilterPanelVisibility: (showFilterPanel: boolean) => void;
  readonly contentSortSelected: (order: ReadonlyArray<IOrderClause>) => void;
  readonly requestSpaceContentsMissingSlice: (start: number, end: number) => void;
  readonly addFieldToSortOrder: (field: string) => void;
  readonly clearMixedContentFilters: () => void;
}

export type ExplorerToolbarVisibility = 'enabled' | 'disabledButtonsOnRight' | 'hidden';

export interface PropsForExplorerToolbar {
  readonly supportFilterPanel: boolean;
  readonly hideSelection?: boolean;
  readonly toolbarContentFilterControl?: JSX.Element;
  readonly toolbarContentSpecificControls?: ContentSpecificControls;
  readonly toolbarVisibility: ExplorerToolbarVisibility;
}
export interface ExplorerOwnProps {
  readonly angularContext: AngularContext;
  readonly supportedDisplayModes: ReadonlyArray<DisplayMode>;
  readonly emptyStateProps?: ExplorerEmptyStateProps;
  readonly hideSelectionAndActionMenu?: boolean;
  readonly toolbarProps: PropsForExplorerToolbar;
}

export type ExplorerProps = ExplorerStateProps & ExplorerDispatchProps & ExplorerOwnProps;

export class Explorer extends React.PureComponent<ExplorerProps> {

  constructor(props: ExplorerProps) {
    super(props);
  }

  public componentDidMount() {
    // we want to know when we are in list or thumbnail view each time we change between them
    // or if we are in a new space/subspace
    this.sendShowExplorerEvent();
  }

  public componentDidUpdate(prevProps: ExplorerProps) {
    if (this.displayModeChanged(prevProps, this.props) || this.spaceChanged(prevProps, this.props)) {
      // we want to know when we are in list or thumbnail view each time we change between them
      // or if we are in a new space/subspace
      this.sendShowExplorerEvent();
    }
  }

  private spaceChanged(prevProps: ExplorerProps, newProps: ExplorerProps): boolean {
    return newProps.spaceInfo.spaceType    !== prevProps.spaceInfo.spaceType
        || newProps.spaceInfo.subspaceName !== prevProps.spaceInfo.subspaceName
        || newProps.spaceInfo.item         !== prevProps.spaceInfo.item
        || (newProps.spaceInfo.item && prevProps.spaceInfo.item
            && newProps.spaceInfo.item.id  !== prevProps.spaceInfo.item.id);
  }

  private displayModeChanged(prevProps: ExplorerProps, newProps: ExplorerProps) {
    return newProps.explorerViewMode !== prevProps.explorerViewMode;
  }

  private sendShowExplorerEvent() {
    Telemetry.sendEvent<ShowExplorer>(CreateExplorerEvent<ShowExplorer>({
      type: TelemetryEvents.ShowExplorer
    }));
  }

  public render(): JSX.Element {
    const displayMode = this.getDisplayMode();
    const ShowFilterPanel = this.props.toolbarProps.supportFilterPanel && this.props.showFilterPanel;
    const ContentAreaClassNames = ExplorerStyles.getContentAreaClassNames(ShowFilterPanel);
    const FilterPanelClassNames = ExplorerStyles.getAdvancedFilterPanelClassNames(ShowFilterPanel);

    return (
      <div className={ExplorerStyles.ExplorerContainer} data-tb-test-id={ExplorerTestIds.Explorer}>
        {(this.props.toolbarProps.toolbarVisibility !== 'hidden') &&
          <ExplorerToolbar selectedItems={this.props.selectedItems}
            clearAllSelections={this.props.clearAllSelections}
            selectAll={this.props.selectAll}
            isSelectionBusy={this.props.isSelectionBusy}
            contentFilter={this.props.contentTypeFilterValues}
            advancedFilterButtonClickHandler={this.toggleAdvancedFilterPanel}
            currentViewMode={this.props.explorerViewMode}
            changeViewMode={this.changeViewMode}
            contentSort={this.props.contentSort}
            contentSortSelected={this.props.contentSortSelected}
            showDatasourceViewTypeDropDown={this.showDatasourceViewTypeDropDown()}
            datasourceViewTypeSelected={this.datasourceViewTypeSelected}
            hasTextSearchParameter={this.hasTextFilter()}
            filtersApplied={this.hasFiltersApplied()}
            itemCount={this.props.items.length}
            showAdvancedFilterButton={this.props.toolbarProps.supportFilterPanel || (this.props.toolbarProps.toolbarVisibility === 'disabledButtonsOnRight')}
              // if the toolbar is disabled, show the filter button (but disabled) instead of hiding it
            spaceInfo={this.props.spaceInfo}
            supportedDisplayModes={this.props.supportedDisplayModes}
            contentSpecificControls={this.props.toolbarProps.toolbarContentSpecificControls}
            contentFilterControl={this.props.toolbarProps.toolbarContentFilterControl}
            hideSelection={this.props.toolbarProps.hideSelection}
            disableButtonsOnRight={this.props.toolbarProps.toolbarVisibility === 'disabledButtonsOnRight'}
          />
        }

        <div className={this.getMainAreaClassNames(displayMode)}
             data-tb-test-id={ExplorerTestIds.ExplorerMainArea}>
          <div className={ ContentAreaClassNames } data-tb-test-id={ExplorerTestIds.ExplorerContentArea}>
            { this.renderContentSection(displayMode) }
            { this.renderSpinner() }
            { this.renderNoContentSection() }
          </div>

          <div className={ FilterPanelClassNames } data-tb-test-id={ AdvancedFilterPanel.TestId }>
            {this.renderAdvancedFilterPanel()}
          </div>
        </div>
      </div>
    );
  }

  private getMainAreaClassNames(displayMode: DisplayMode) {
    const classNames = [ ExplorerStyles.ExplorerMainArea ];

    if (displayMode === 'thumbnail') {
      classNames.push(ExplorerStyles.ExplorerMainAreaForCardView);
    }

    return classNames.join(' ');
  }

  private hasFiltersApplied = (): boolean => {
    const numberOfFilters = _.size(this.props.mixedContentFilterMap);
    const inSearchSpaceWithOnlySearchFilter = isSomeKindaSearchSpace(this.props.spaceInfo) && numberOfFilters === 1 && this.hasTextFilter();
    return numberOfFilters > 0 && !inSearchSpaceWithOnlySearchFilter;
  }

  private hasTextFilter = (): boolean => {
    return !_.isUndefined(this.props.mixedContentFilterMap['search']);
  }

  private toggleAdvancedFilterPanel = () => {
    Telemetry.sendEvent<AdvancedFilterPanelToggleEvent>({
      type: TelemetryEvents.AdvancedFilterPanelToggle,
      openOrClose: this.props.showFilterPanel ? 'close' : 'open',
      mixedContentEnabled: true,
    });

    this.props.updateFilterPanelVisibility(!this.props.showFilterPanel);
  }

  private changeViewMode = (viewMode: DisplayMode) => {
    this.props.changeExplorerViewMode(viewMode, true);
  }

  private requestSpaceContentsMissingSlice = (start: number, end: number) => {
    this.props.requestSpaceContentsMissingSlice(start, end);
  }

  private showDatasourceViewTypeDropDown = () => {
    const isShowingOnlyDatasourcesOnSpecificPages = (isProjectSpace(this.props.spaceInfo) || isSiteSpace(this.props.spaceInfo) || isSiteUserContentSubspace(this.props.spaceInfo))
      && this.props.contentTypeFilterValues.length === 1
      && (this.props.contentTypeFilterValues[0] === 'datasource'
        || this.props.contentTypeFilterValues[0] === 'dataconnection');

    return isShowingOnlyDatasourcesOnSpecificPages || IsWorkbookDatasourceSubspaceTab(this.props.spaceInfo);
  }

  private datasourceViewTypeSelected = (viewType: DatasourceViewTypeInfo): void => {
    // We filter to the specific type selected by user
    if (IsWorkbookDatasourceSubspaceTab(this.props.spaceInfo)) {
      const newSubspaceName = contentTypeToWorkbookSubspaceName(viewType.type) as WorkbookSubspaceName;
      if (newSubspaceName !== this.props.spaceInfo.subspaceName) {
        const url = newSubspaceName === 'datasources' ?
          this.props.urls.workbookDatasourcesUrl(this.props.spaceInfo.item.id) :
          getWorkbookUrl(this.props.urls, this.props.spaceInfo.item.id, 'dataconnections');

        Location.instance.setUrl(url);
      }

    } else if (isProjectSpace(this.props.spaceInfo) || isSiteSpace(this.props.spaceInfo) || isSiteUserContentSubspace(this.props.spaceInfo)) {
      this.props.filterSelected([viewType.type]);

    }
  }

  private renderContentSection(displayMode: DisplayMode) {
    let contentElement: React.ReactElement;
    if (displayMode === 'thumbnail') {
      contentElement = (
        <ConnectedCardView
          showActionMenu={!this.props.hideSelectionAndActionMenu}
          // disable checkbox-selection in recommendations page
          showSelection={!this.props.toolbarProps.hideSelection}
        />
      );
    } else if (displayMode === 'list') {
        // Determined if the current user is an admin for the server or site
        // default to false if we can't find AuthenticationService
        const sessionManagementService = getSessionManagementServiceInstance();
        const isSessionSiteOrServerAdmin = sessionManagementService ?
              sessionManagementService.isSessionSiteOrServerAdmin() : false;
        contentElement = (
        <ConnectedListView
          urls={this.props.urls}
          items={this.props.items}
          selectedItems={this.props.selectedItems}
          selectItem={this.props.selectItem}
          deselectItem={this.props.deselectItem}
          requestSpaceContentsMissingSlice={this.requestSpaceContentsMissingSlice}
          contentFilter={this.props.contentTypeFilterValues}
          spaceInfo={this.props.spaceInfo}
          angularContext={this.props.angularContext}
          isBusyFetchingItems={this.props.isBusyFetchingItems}
          currentSort={this.props.contentSort}
          changeSort={this.props.addFieldToSortOrder}
          setSortOrder={this.props.contentSortSelected}
          isSessionSiteOrServerAdmin={isSessionSiteOrServerAdmin}
          isGuestEnabled={ServerSettingsUnauthenticated.isGuestEnabled()}
        />
      );
    }

    if(['thumbnail', 'list'].includes(displayMode)) {
      return (
        <ContentClickedTelemetryContext.Provider value={generateContentClickedTelemetryContext(this.props.spaceInfo, this.props.items)}>
          <FavoriteToggleClickedTelemetryContext.Provider value={generateFavoriteToggleClickedTelemetryContext(this.props.spaceInfo, this.props.items)}>
            <ActionMenuItemClickedTelemetryContext.Provider value={generateActionMenuItemClickedTelemetryContext(this.props.spaceInfo, this.props.items)}>
              <ExplorePageDisplayModeContext.Provider value={displayMode === 'thumbnail' ? ExploreDisplayMode.Grid: ExploreDisplayMode.List}>
                {contentElement}
              </ExplorePageDisplayModeContext.Provider>
            </ActionMenuItemClickedTelemetryContext.Provider>
          </FavoriteToggleClickedTelemetryContext.Provider>
        </ContentClickedTelemetryContext.Provider>
      );
    }

    return <div>{ Messages.errorUhOh() }</div>; // This should never happen
  }

  private renderAdvancedFilterPanel = () => {
    const props = {
      angularContext: this.props.angularContext,
      mixedContentFilterMap: this.props.mixedContentFilterMap,
      spaceInfo: this.props.spaceInfo,
      shownContentTypes: this.props.contentTypeFilterValues,
    };
    if (isSiteUsersSpace(this.props.spaceInfo) || isGroupSpace(this.props.spaceInfo)) {
      return <SiteUsersAdvancedFilterPanel {...props}/>;
    } else if (isAllServerUsersSpace(this.props.spaceInfo)) {
      return <ServerUsersAdvancedFilterPanel {...props}/>;
    }
    return <AdvancedFilterPanel {...props}/>;
  }

  private getDisplayMode(): DisplayMode {
    const explorerDisplayMode = this.props.explorerViewMode;
    const supportedDisplayModes = this.props.supportedDisplayModes;

    if (supportedDisplayModes.includes(explorerDisplayMode)) {
      return explorerDisplayMode;
    } else if (supportedDisplayModes.length > 0) {
      return supportedDisplayModes[0];
    }

    return 'list';
  }

  private renderSpinner(): JSX.Element {

    return (
      this.props.isBusyFetchingItems &&
        <div className={ExplorerStyles.ListViewWaitingDiv}>
          <div style={{paddingTop: 48}}>
            <ActivitySpinnerWidget testId={ExplorerTestIds.ExplorerBusySpinner} />
          </div>
        </div>
    );
  }

  private clearMixedContentFilters = (): void => {
    this.props.clearMixedContentFilters();
  }

  private renderNoContentSection(): JSX.Element {
    if (!this.props.isBusyFetchingItems && this.props.items.length === 0) {
      // we display no filter empty state message first.
      // then we display any custom message from props
      // default custom message

      if (this.props.toolbarProps.supportFilterPanel && this.hasFiltersApplied()) {
        return (
          <ExplorerEmptyState
            dataTestId={ExplorerTestIds.ExplorerNoContentFilters}
            iconUrl={FILTER_ICON_LARGE_PATH}
            primaryMessage={Messages.contentEmptyFilterMessage()}
            secondaryMessage={''}
            actionButtonMessage={Messages.contentEmptyClearFilters()}
            onActionButton={this.clearMixedContentFilters}
          />
        );
      }

      if (this.props.emptyStateProps) {
        return <ExplorerEmptyState {...this.props.emptyStateProps} />;
      }

      if (this.props.contentTypeFilterValues.length === 1) {
        return (
          <ExplorerEmptyState
            dataTestId={ExplorerTestIds.ExplorerNoContent}
            iconUrl={''}
            primaryMessage={ExplorableItemDefinitions[this.props.contentTypeFilterValues[0]].emptyStateMessage()}
            secondaryMessage={''}
          />
        );
      }

      return (
        <ExplorerEmptyState
            dataTestId={ExplorerTestIds.ExplorerNoContent}
            iconUrl={''}
            primaryMessage={Messages.contentEmptyMessage()}
            secondaryMessage={''}
        />
      );
    } else {
      return null;
    }
  }
}

export function generateContentClickedTelemetryContext(spaceInfo: SpaceInfo, allContents: ReadonlyArray<IExplorableItemTypeInfo>): ContentClickedTelemetryContextValueType {
  return function ContentClickedTelemetryContext(childFunction: ContentClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <RelatedContentInfoContext.Consumer>
            {relatedContentInfo =>
              <ExplorePageDisplayModeContext.Consumer>
                {displayMode =>
                  <CardPositionContext.Consumer>
                    {contentPosition =>
                      <SearchTermDataContext.Consumer>
                        {searchTerm =>
                          childFunction(() => {
                            const eventHandler = generateTelemetryEventHandler(
                              TelemetryEvents.ContentClicked,
                              spaceInfo,
                              allContents,
                              contentInfo,
                              ContentLocation.Explore,
                              contentPosition,
                              {
                                displayMode: displayMode,
                                contentTotal: allContents.length,
                                searchTerm: searchTerm,
                                relatedContentId: relatedContentInfo && relatedContentInfo.relatedContentId,
                                // Known bug on TS prevents using the commented line and we have to use an if statemnte
                                // https://github.com/microsoft/TypeScript/issues/32455
                                // relatedContentType: relatedContentInfo && relatedContentInfo.relatedContentType,
                                relatedContentType: relatedContentInfo? relatedContentInfo.relatedContentType : null
                              }
                            );
                            eventHandler();
                          })
                        }
                      </SearchTermDataContext.Consumer>
                    }
                  </CardPositionContext.Consumer>
                }
              </ExplorePageDisplayModeContext.Consumer>
            }
          </RelatedContentInfoContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}

export function generateFavoriteToggleClickedTelemetryContext(spaceInfo: SpaceInfo, allContents: ReadonlyArray<IExplorableItemTypeInfo>) {
  return function FavoriteToggleClickedTelemetryContext(childFunction: FavoriteToggleClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <ExplorePageDisplayModeContext.Consumer>
            {displayMode =>
              <CardPositionContext.Consumer>
                {contentPosition =>
                  <SearchTermDataContext.Consumer>
                    {searchTerm =>
                      childFunction((toggleType: FavoriteToggleType) => {
                        const eventHandler = generateTelemetryEventHandler(
                          TelemetryEvents.FavoriteToggleClicked,
                          spaceInfo,
                          allContents,
                          contentInfo,
                          ContentLocation.Explore,
                          contentPosition,
                          {displayMode: displayMode, contentTotal: allContents.length, searchTerm: searchTerm}
                        );
                        eventHandler(toggleType);
                      })
                    }
                  </SearchTermDataContext.Consumer>
                }
              </CardPositionContext.Consumer>
            }
          </ExplorePageDisplayModeContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}

export function generateActionMenuItemClickedTelemetryContext(spaceInfo: SpaceInfo, allContents: ReadonlyArray<IExplorableItemTypeInfo>) {
  return function ActionMenuItemClickedTelemetryContext(childFunction: ActionMenuItemClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <ExplorePageDisplayModeContext.Consumer>
            {displayMode =>
              <CardPositionContext.Consumer>
                {contentPosition =>
                  <SearchTermDataContext.Consumer>
                    {searchTerm =>
                      childFunction((actionType: string) => {
                        const eventHandler = generateTelemetryEventHandler(
                          TelemetryEvents.ActionMenuItemClicked,
                          spaceInfo,
                          allContents,
                          contentInfo,
                          ContentLocation.Explore,
                          contentPosition,
                          {displayMode: displayMode, contentTotal: allContents.length, searchTerm: searchTerm}
                        );
                        eventHandler(actionType);
                      })
                    }
                  </SearchTermDataContext.Consumer>
                }
              </CardPositionContext.Consumer>
            }
          </ExplorePageDisplayModeContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}

export namespace ExplorerTestIds {
  export const Explorer = 'tb-content-explorer';
  export const ExplorerBusySpinner = 'explorer-view-busy';
  export const ExplorerNoContent = 'tb-spaces-no-content';
  export const ExplorerNoContentFilters = 'tb-spaces-no-content-filters';
  export const ExplorerMainArea = 'explorer-main-area';
  export const ExplorerContentArea = 'explorer-content-area';
}
